// src/components/Header.tsx
"use client";

import classNames from "classnames";
import Image from "next/image";
import React, { useEffect, useState, useCallback } from "react";
import { useTranslations } from "@/context/TranslationsContext";
import { useCurrency } from "@/context/CurrencyContext";

import CloudflareInsights from "./CloudflareInsights";
import LocalizedLink from "./LocalizedLink";
import logger from "@/utils/logger.client";

let hasFetchedCurrency = false;

const Header: React.FC = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [LoginModalComponent, setLoginModalComponent] = useState<
    React.FC<{ onClose: () => void }> | null
  >(null);

  const { t, direction } = useTranslations();
  const isRTL = direction === "rtl";

  const { currencyCode, exchangeRate } = useCurrency();

  useEffect(() => {
    async function fetchAndStoreCurrency() {
      if (hasFetchedCurrency) return;
      hasFetchedCurrency = true;

      try {
        const res = await fetch("/api/currency", {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) {
          logger.error("Header: failed to fetch currency API", {
            status: res.statusText,
          });
          return;
        }
        await res.json();
        logger.info("Header: successfully fetched currency");
      } catch (err: any) {
        logger.warn("Header: currency fetch threw, skipping update", {
          error: err.message ?? err,
        });
      }
    }

    fetchAndStoreCurrency();
  }, []);

  const openLoginModal = useCallback(async () => {
    try {
      if (!LoginModalComponent) {
        const mod = await import("./LoginModal");
        setLoginModalComponent(() => mod.default);
      }
      setIsLoginModalOpen(true);
    } catch (err: any) {
      logger.error("Header: failed to load LoginModal component", {
        error: err.message ?? err,
      });
    }
  }, [LoginModalComponent]);

  const closeLoginModal = useCallback(() => setIsLoginModalOpen(false), []);

  const loginLabel = t("header.login");
  const homeLabel = t("header.home");
  const logoAlt = t("header.logoAlt");

  return (
    <>
      <CloudflareInsights />

      <header
        className={classNames(
          "shadow-md p-2 sm:p-4 flex items-center mt-2 mb-2 sm:mb-4 mx-2 sm:mx-4 rounded-lg bg-primary justify-between",
          { "flex-row-reverse": isRTL }
        )}
      >
        <div className="flex-shrink-0">
          <LocalizedLink href="/" aria-label={homeLabel} className="block">
            <div className="relative w-auto h-auto">
              <Image
                src="/images/large-logo-w.png"
                alt={logoAlt}
                width={160}
                height={40}
                sizes="(max-width: 640px) 8rem, 10rem"
                priority
              />
            </div>
          </LocalizedLink>
        </div>

        <div className="flex-1 hidden sm:block" />

        <button
          onClick={openLoginModal}
          className="px-3 py-1.5 bg-white text-primary font-semibold rounded-md hover:bg-gray-200 transition-colors text-sm sm:text-base whitespace-nowrap"
          aria-label={loginLabel}
        >
          {loginLabel}
        </button>

        {isLoginModalOpen && LoginModalComponent && (
          <LoginModalComponent onClose={closeLoginModal} />
        )}
      </header>
    </>
  );
};

export default React.memo(Header);
